import { EmptyState } from "@/components/EmptyState";
import { ChatIcon } from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "cn";
import { useEffect, useMemo, useState } from "react";

import { errorMessage } from "../lib/query.ts";

import { uploadAttachment, type UploadedAttachment } from "./attachments.ts";
import { Composer } from "./components/Composer.tsx";
import { Message } from "./components/Message.tsx";
import { registerChatComponents } from "./components/register-components.ts";
import {
  ChatSurfaceProvider,
  messageAnchor,
  type ChatDensity,
} from "./surface.tsx";
import type { UseAgentChatResult } from "./use-agent-chat.ts";
import { useStickToBottom } from "./use-stick-to-bottom.ts";

registerChatComponents();

interface Props {
  readonly chat: UseAgentChatResult;
  /** A pantalla completa o en la ventana pequeña de Work. */
  readonly density: ChatDensity;
  /** Solo en la ventana pequeña: saltar al chat en ese mensaje. */
  readonly onOpenInChat?: (messageId: string) => void;
  /** Un mensaje al que hay que ir en cuanto se pueda. */
  readonly focusMessageId?: string | null;
  onFocusHandled?: () => void;
}

/**
 * El hilo y el compositor. UNO SOLO, en dos anchos.
 *
 * Lo monta el modo Chat a pantalla completa y la ventana pequeña de Work en
 * compacto, y son EL MISMO chat: el estado viene por props desde el armazón
 * (§9.2), así que no hay dos conversaciones vivas ni dos streams. Cambiar de
 * sitio no pierde el turno en curso ni obliga a repetir nada.
 *
 * Lo que cambia con la densidad es el ancho de la columna de lectura, el aire
 * entre mensajes y qué versión dibuja cada componente del agente; lo que NO
 * cambia es el hilo, las tarjetas de confirmación ni el compositor.
 */
export function ChatThread({
  chat,
  density,
  onOpenInChat,
  focusMessageId = null,
  onFocusHandled,
}: Props) {
  const {
    messages,
    status,
    error,
    answered,
    decisions,
    outcomes,
    send,
    resolveConfirmation,
    answerQuestions,
    stop,
    clearError,
    ensureConversation,
    compacted,
    loadingHistory,
    historyError,
  } = chat;

  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const compact = density === "compact";

  const decisionState = useMemo(
    () => ({ answered, decisions, outcomes }),
    [answered, decisions, outcomes],
  );

  /**
   * Señal de "ha cambiado el contenido" para el seguimiento del scroll.
   *
   * Cuenta las partes, no los mensajes: durante un turno el número de mensajes
   * no cambia, pero el contenido crece con cada fragmento y cada herramienta.
   */
  const contentVersion = useMemo(
    () =>
      messages.reduce(
        (total, message) => total + message.parts.length,
        messages.length,
      ),
    [messages],
  );

  const { containerRef, atBottom, scrollToBottom } =
    useStickToBottom<HTMLDivElement>(contentVersion);

  /**
   * Ir a un mensaje concreto, cuando se viene de "ver en el chat".
   *
   * Depende también de cuántos mensajes hay porque al llegar desde la ventana
   * pequeña la conversación puede estar todavía cargándose: mientras el ancla
   * no exista, se vuelve a intentar en cuanto llega algo.
   */
  useEffect(() => {
    if (focusMessageId === null) return;
    const anchor = document.getElementById(messageAnchor(focusMessageId));
    if (anchor === null) return;
    anchor.scrollIntoView({ block: "start" });
    onFocusHandled?.();
  }, [focusMessageId, messages.length, onFocusHandled]);

  const busy = status === "submitted" || status === "streaming";
  const empty = messages.length === 0 && !loadingHistory;

  const attach = (file: File) => {
    setUploading(true);
    setUploadError(null);

    ensureConversation()
      .then((conversationId) => uploadAttachment(conversationId, file))
      .then((uploaded) => {
        setAttachments((current) => [...current, uploaded]);
      })
      .catch((cause: unknown) => {
        setUploadError(errorMessage(cause));
      })
      .finally(() => {
        setUploading(false);
      });
  };

  /**
   * Al enviar, se le dice al agente qué archivos acaba de subir el usuario. El
   * contenido NO viaja: solo el nombre y el id, para que pueda leerlo con su
   * herramienta si hace falta.
   */
  const sendWithAttachments = (text: string) => {
    const preface =
      attachments.length === 0
        ? ""
        : `${attachments
            .map(
              (file) =>
                `[Archivo adjunto: "${file.fileName}" (id ${file.attachmentId})]`,
            )
            .join("\n")}\n\n`;

    send(`${preface}${text}`);
    setAttachments([]);
    // Al enviar SIEMPRE se baja: el usuario acaba de actuar.
    scrollToBottom(true);
  };

  const composer = (
    <Composer
      status={status}
      attachments={attachments}
      uploading={uploading}
      autoFocus={compact}
      onSend={sendWithAttachments}
      onStop={stop}
      onAttach={attach}
      onRemoveAttachment={(id) => {
        setAttachments((current) =>
          current.filter((file) => file.attachmentId !== id),
        );
      }}
    />
  );

  /**
   * A pantalla completa y sin mensajes, el compositor va en el CENTRO y no
   * abajo: es lo único que hay que hacer, así que pedirle al usuario que lo
   * busque al pie de una pantalla vacía sería raro. En la ventana pequeña no:
   * ahí el compositor vive abajo siempre, porque la ventana es estrecha y
   * moverlo la haría parecer otra cosa.
   */
  if (empty && !compact) {
    return (
      <div className="grid min-h-0 min-w-0 flex-1 place-items-center p-4">
        <div className="w-full max-w-3xl text-center">
          <h2 className="mb-6 text-2xl font-medium tracking-tight">
            ¿Qué necesitas?
          </h2>
          <div className="text-left">{composer}</div>
          <p className="mx-auto mt-4 max-w-lg text-sm text-muted-foreground">
            Adjunta un Excel y pídeme que lo revise, o pregúntame por los
            participantes que ya estén cargados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ChatSurfaceProvider density={density} openInChat={onOpenInChat}>
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        {/* El área que scrollea ocupa TODO el ancho --por eso la barra queda
            pegada al borde, como en cualquier aplicación-- y lo que se centra
            dentro es la columna de lectura. */}
        <div
          className={cn(
            "scroll-quiet min-h-0 min-w-0 flex-1 overflow-auto",
            compact ? "px-3 pt-3" : "px-4 pt-6",
          )}
          ref={containerRef}
        >
          <div className={compact ? COMPACT_COLUMN : COLUMN}>
            {loadingHistory && (
              <p className="text-sm text-muted-foreground">
                Recuperando la conversación…
              </p>
            )}

            {empty && compact && (
              <EmptyState icon={ChatIcon} className="py-6">
                Pregúntame por lo que estás viendo sin salir de esta pantalla.
              </EmptyState>
            )}

            {compacted !== undefined && (
              <details className="rounded-lg border bg-secondary/40 px-3 py-2 text-sm text-muted-foreground">
                <summary className="cursor-pointer">
                  Los primeros {compacted.count} mensajes están resumidos para
                  que el asistente pueda seguir
                </summary>
                <p className="mt-2 whitespace-pre-wrap text-foreground">
                  {compacted.summary}
                </p>
              </details>
            )}

            {messages.map((message) => (
              <Message
                key={message.id}
                message={message}
                decisionState={decisionState}
                onResolveConfirmation={(actionId, approved) => {
                  resolveConfirmation(actionId, approved);
                  scrollToBottom(true);
                }}
                onAnswerQuestions={(questionId, answers, note) => {
                  answerQuestions(questionId, answers, note);
                  scrollToBottom(true);
                }}
                busy={busy}
              />
            ))}

            {status === "submitted" && <Thinking />}

            {historyError !== undefined && (
              <Alert variant="destructive">
                <AlertTitle>No se pudo abrir la conversación</AlertTitle>
                <AlertDescription>
                  {errorMessage(historyError)}
                </AlertDescription>
              </Alert>
            )}

            {uploadError !== null && (
              <Alert variant="destructive">
                <AlertTitle>No se pudo subir el archivo</AlertTitle>
                <AlertDescription>{uploadError}</AlertDescription>
              </Alert>
            )}

            {error !== undefined && (
              <Alert variant="destructive">
                <AlertTitle>No se pudo completar el turno</AlertTitle>
                <AlertDescription className="grid items-start gap-2">
                  <p>{errorMessage(error)}</p>
                  <Button variant="outline" size="sm" onClick={clearError}>
                    Entendido
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        {/* Solo aparece si el usuario se salió del final: volver es cosa suya. */}
        {!atBottom && (
          <button
            type="button"
            className={cn(
              "absolute left-1/2 z-2 -translate-x-1/2 rounded-full border bg-card px-3.5 py-1.5 text-xs shadow-lg hover:border-primary",
              compact ? "bottom-18" : "bottom-22",
            )}
            onClick={() => {
              scrollToBottom(true);
            }}
          >
            {busy ? "Sigue escribiendo ↓" : "Ir al final ↓"}
          </button>
        )}

        <div className={cn("flex-none", compact ? "p-3" : "px-4 pt-3 pb-4")}>
          <div className={compact ? COMPACT_COLUMN : COLUMN}>{composer}</div>
        </div>
      </div>
    </ChatSurfaceProvider>
  );
}

/** La columna de lectura: a 46rem el texto se sigue con la vista. */
const COLUMN = "mx-auto flex min-w-0 max-w-3xl flex-col gap-6";

/** En la ventana pequeña no hay columna que centrar: es toda la columna. */
const COMPACT_COLUMN = "flex min-w-0 flex-col gap-4";

/** Los tres puntos mientras el turno no ha empezado a escribir. */
function Thinking() {
  return (
    <div className="flex gap-1.5 pl-0.5" aria-label="Pensando">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="size-1.5 animate-[pulse_1.2s_ease-in-out_infinite] rounded-full bg-muted-foreground motion-reduce:animate-none motion-reduce:opacity-70"
          style={{ animationDelay: `${String(delay)}ms` }}
        />
      ))}
    </div>
  );
}
